// =============================================================================
// useCalibration — Tinder-style verification hook for CopyTwin clones
//
// Drives the calibration deck UI:
//   1. fetchSamples(categoryIds, perCategory) → calls the Cloud Function and
//      gets back fresh AI-generated example sentences in the clone's voice.
//   2. submitFeedback(events) → aggregates Tinder swipes into one RTDB
//      patch (cheap: one write per session, not per card) using
//      aggregateCalibrationEvents from cloneUtils.
//
// We deliberately do NOT round-trip the Cloud Function for each swipe —
// the AI generation IS the expensive part, the persistence is trivial.
// Direct RTDB write keeps the deck snappy and works offline-tolerant
// (swipes locally, syncs when reconnected via Firebase's queue).
// =============================================================================

import { useCallback, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { ref, update } from "firebase/database";
import { functions, database } from "../../firebaseConfig";
import {
  aggregateCalibrationEvents,
  createEmptyCalibration,
  toggleHardReject,
  aggregateSubjectCalibrationEvents,
  createEmptySubjectCalibration,
  toggleSubjectHardReject,
  computeCategoryRoundCounts,
  applyCalibrationDuelVerdicts,
} from "./cloneUtils";

// Body-calibration callables (default mode).
const calibrationCallable = httpsCallable(functions, "generateCalibrationSamples", {
  timeout: 90000,
});
const distillCallable = httpsCallable(functions, "distillCalibrationPreferences", {
  timeout: 90000,
});
// A/B-duel refinement (body only) — generates sharper challengers for the
// already-approved sentences so the user can refine a locked-in calibration.
const duelsCallable = httpsCallable(functions, "generateCalibrationDuels", {
  timeout: 120000,
});

// Subject-calibration callables (mode: 'subject').
const subjectCalibrationCallable = httpsCallable(functions, "generateSubjectCalibrationSamples", {
  timeout: 90000,
});
const subjectDistillCallable = httpsCallable(functions, "distillSubjectCalibrationPreferences", {
  timeout: 90000,
});

// Mode → wiring. The hook is identical for body and subject calibration; only
// the cloud functions, the RTDB sub-path and the pure aggregation helpers
// differ. Selecting them up-front keeps the hook body mode-agnostic.
const MODE_CONFIG = {
  body: {
    rtdbKey: "calibration",
    samplesFn: calibrationCallable,
    distillFn: distillCallable,
    aggregate: aggregateCalibrationEvents,
    createEmpty: createEmptyCalibration,
    toggleReject: toggleHardReject,
  },
  subject: {
    rtdbKey: "subjectCalibration",
    samplesFn: subjectCalibrationCallable,
    distillFn: subjectDistillCallable,
    aggregate: aggregateSubjectCalibrationEvents,
    createEmpty: createEmptySubjectCalibration,
    toggleReject: toggleSubjectHardReject,
  },
};

// =============================================================================
// FAILURE CLASSIFICATION
//
// httpsCallable normalises EVERY transport failure into a `FirebaseError`
// with code `functions/internal`. That's catastrophic for UX: a CORS
// rejection because the function isn't deployed, a 503 because the
// container is cold-starting, a 429 from over-rate, and a genuine bug in
// the function body all look identical to React. So we sniff the
// underlying signal and translate it into something the deck UI can act
// on (auto-retry vs. "please try again later" vs. "contact support").
//
// Why this matters operationally: we already had one production outage
// where a half-deployed Gen-1 function passed `firebase functions:list`
// (showed up in the registry) but failed every preflight (no runtime
// container) — and the deck showed an endless spinner because every
// invocation returned `internal`. With classification, the same scenario
// would surface as "Calibration service is currently unreachable. Retry?"
// instead.
// =============================================================================

const FETCH_RETRY_MAX = 2;          // 1 initial + 2 retries on transient
const FETCH_RETRY_BASE_MS = 1200;   // exponential backoff seed

const classifyError = (err) => {
  const code = String(err?.code || '').toLowerCase();
  const message = String(err?.message || '').toLowerCase();
  // 1. Auth — never retry, the user must re-login.
  if (code.includes('unauthenticated') || code.includes('permission-denied')) {
    return { kind: 'auth', retryable: false, friendly: 'You need to be logged in to calibrate.' };
  }
  // 2. Pre-condition (clone not trained / clone gone) — user fixable.
  if (code.includes('failed-precondition') || code.includes('not-found')) {
    return { kind: 'precondition', retryable: false, friendly: err.message || 'Calibration is not available for this clone yet.' };
  }
  if (code.includes('invalid-argument')) {
    return { kind: 'invalid', retryable: false, friendly: err.message || 'Invalid calibration request.' };
  }
  // 3. Transport / outage — retryable. The signature signals for these
  // get swallowed by httpsCallable into a generic `internal`, so we
  // pattern-match on the message text.
  const isTransport =
    code.includes('unavailable') ||
    code.includes('deadline-exceeded') ||
    code.includes('aborted') ||
    code.includes('resource-exhausted') ||
    code === 'functions/internal' ||
    message.includes('cors') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('preflight');
  if (isTransport) {
    return {
      kind: 'transport',
      retryable: true,
      friendly:
        'Calibration service is currently unreachable. ' +
        'This is usually temporary — please try again in a moment.',
    };
  }
  // 4. Everything else is treated as a real bug; one retry, then surface.
  return {
    kind: 'unknown',
    retryable: true,
    friendly: err.message || 'Could not generate calibration samples.',
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useCalibration = (userId, { mode = "body" } = {}) => {
  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.body;
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [lastBatch, setLastBatch] = useState(null);
  // REAL generation progress across the focused group calls. null when idle.
  // { done, total } — drives the honest progress bar in the deck (each
  // completed group call bumps `done`), instead of a pure time estimate.
  const [genProgress, setGenProgress] = useState(null);

  // SHADOW STATE — the last calibration object we wrote to RTDB, keyed by
  // cloneId. We use this as the authoritative source for the NEXT batch
  // when the RTDB onValue listener hasn't yet propagated our previous
  // write back into the live clone snapshot the editor passes in.
  //
  // Why this matters: a 14-card session flushes in batches of 4. Between
  // batches the React tree might still render the OLD calibration prop
  // (RTDB roundtrip latency ~50-300ms). Without this ref, batch 2's
  // aggregation starts from the pre-batch-1 baseline and silently wipes
  // the events from batch 1. Bug observed at scale: 30%+ of swipes lost.
  //
  // The ref is cleared when the cloneId changes (different clone, fresh
  // state) but otherwise persists across the whole session.
  const lastWrittenRef = useRef({ cloneId: null, calibration: null });

  // Promise of the most recent feedback write. Distillation awaits this so
  // the cloud function never reads a stale calibration snapshot (the write
  // and the auto-distill are both fire-and-forget from the deck, so without
  // this the distiller could race ahead of the final swipe batch).
  const lastWriteRef = useRef(Promise.resolve());

  const [distilling, setDistilling] = useState(false);

  // CORE single-call fetch with exponential-backoff retry. Stateless w.r.t.
  // the global `generating` / `error` UI flags — it just returns
  // { data, cls }, leaving the caller (single vs. grouped) to decide how to
  // surface success/failure. ONLY retries transport / unknown failures; auth
  // and precondition errors bail immediately (a retry can't fix them).
  const runFetchWithRetry = useCallback(
    async (payload) => {
      let lastErr = null;
      for (let attempt = 0; attempt <= FETCH_RETRY_MAX; attempt += 1) {
        try {
          const result = await cfg.samplesFn(payload);
          return { data: result.data, cls: null };
        } catch (err) {
          lastErr = err;
          const cls = classifyError(err);
          console.warn(
            `[useCalibration] generation failed (attempt ${attempt + 1}/${FETCH_RETRY_MAX + 1}, kind=${cls.kind}):`,
            err?.code,
            err?.message,
          );
          if (!cls.retryable || attempt === FETCH_RETRY_MAX) {
            return { data: null, cls };
          }
          await sleep(FETCH_RETRY_BASE_MS * (2 ** attempt));
        }
      }
      return { data: null, cls: classifyError(lastErr || {}) };
    },
    [cfg],
  );

  // Fetch ONE focused batch (single call) — used for a focused single-category
  // top-up or an explicit override set. For a full multi-group round, use
  // fetchSamplesGrouped below.
  const fetchSamples = useCallback(
    async ({ cloneId, categoryIds, perCategory = 3, perCategoryById, language = "English" }) => {
      if (!userId || !cloneId) return null;
      setGenerating(true);
      setError(null);
      setErrorKind(null);
      setGenProgress(null);
      const { data, cls } = await runFetchWithRetry({
        cloneId,
        categoryIds,
        perCategory,
        perCategoryById,
        language,
        userId,
      });
      setGenerating(false);
      if (data) {
        setLastBatch(data);
        return data;
      }
      if (cls) {
        setError(cls.friendly);
        setErrorKind(cls.kind);
      }
      return null;
    },
    [userId, runFetchWithRetry],
  );

  // Fetch a full round as SEVERAL focused group calls IN PARALLEL. Each group
  // is a small, semantically coherent set of categories (e.g. "opening" beats)
  // so the model holds one consistent writing frame per call → sharper, more
  // consistent samples than one 14-category dump. As each group resolves we
  // bump `genProgress.done`, giving the deck an HONEST progress bar (real
  // completions, not a time estimate). Total wall-clock ≈ a single call.
  //
  // Resilience: a single group failing does NOT abort the round — we keep the
  // samples from the groups that succeeded (graceful partial). Only if EVERY
  // group fails do we surface the error.
  const fetchSamplesGrouped = useCallback(
    async ({ cloneId, groups, perCategory = 3, perCategoryById, language = "English" }) => {
      if (!userId || !cloneId || !Array.isArray(groups) || groups.length === 0) {
        return null;
      }
      setGenerating(true);
      setError(null);
      setErrorKind(null);
      setGenProgress({ done: 0, total: groups.length });

      let firstCls = null;
      const results = await Promise.all(
        groups.map(async (categoryIds) => {
          const groupCounts = perCategoryById
            ? Object.fromEntries(
                categoryIds
                  .filter((id) => perCategoryById[id] != null)
                  .map((id) => [id, perCategoryById[id]]),
              )
            : undefined;
          const { data, cls } = await runFetchWithRetry({
            cloneId,
            categoryIds,
            perCategory,
            perCategoryById: groupCounts,
            language,
            userId,
          });
          setGenProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
          if (!data && !firstCls) firstCls = cls;
          return data;
        }),
      );

      setGenerating(false);
      setGenProgress(null);

      const samples = [];
      let resolvedLanguage = language;
      for (const d of results) {
        if (d?.samples?.length) samples.push(...d.samples);
        if (d?.language) resolvedLanguage = d.language;
      }

      if (samples.length === 0) {
        const cls = firstCls || {
          friendly: "Could not generate calibration samples.",
          kind: "unknown",
        };
        setError(cls.friendly);
        setErrorKind(cls.kind);
        return null;
      }

      const merged = { ok: true, samples, language: resolvedLanguage };
      setLastBatch(merged);
      return merged;
    },
    [userId, runFetchWithRetry],
  );

  // Submit a session worth of swipes. `events` is an array of
  // { categoryId, text, approved }. We aggregate against the existing
  // calibration block (caller passes it from the live clone snapshot) and
  // write the result in one patch. Returns the new calibration object so
  // the caller can mirror it into local UI state without waiting for the
  // RTDB roundtrip.
  const submitFeedback = useCallback(
    async ({ cloneId, currentCalibration, events }) => {
      if (!userId || !cloneId || !Array.isArray(events) || events.length === 0) {
        return null;
      }
      // Reset the shadow ref on cloneId switch — we don't want to bleed
      // calibration state from one clone into another's writes.
      if (lastWrittenRef.current.cloneId !== cloneId) {
        lastWrittenRef.current = { cloneId, calibration: null };
      }
      // Prefer the shadow (most-recent-write) over the prop. The prop
      // may still be the pre-write snapshot if RTDB hasn't roundtripped
      // yet; the shadow is guaranteed to include every event we've
      // already flushed this session.
      const base = lastWrittenRef.current.calibration
        || currentCalibration
        || cfg.createEmpty();
      const next = cfg.aggregate(base, events);
      try {
        const writePromise = update(
          ref(database, `/World/${userId}/copyClones/${cloneId}`),
          {
            [cfg.rtdbKey]: next,
            updatedAt: Date.now(),
          },
        );
        // Publish the in-flight write BEFORE awaiting so distillPreferences
        // (which may fire as soon as the deck flips to sessionDone) can chain
        // onto it instead of racing it.
        lastWriteRef.current = writePromise;
        await writePromise;
        lastWrittenRef.current = { cloneId, calibration: next };
        return next;
      } catch (err) {
        console.warn("[useCalibration] feedback write failed:", err);
        setError(err.message || "Could not save calibration feedback");
        setErrorKind('write');
        return null;
      }
    },
    [userId, cfg],
  );

  // Distill the accumulated swipes into a STYLE PREFERENCE PROFILE. Fired
  // automatically at session end. Best-effort and non-blocking: it waits
  // for the final feedback write to land, then asks the backend to read the
  // swipes and write back calibration.preferenceProfile. Failures are
  // swallowed (the profile just won't update this round) — calibration must
  // never feel broken because a background analysis hiccuped.
  const distillPreferences = useCallback(
    async ({ cloneId }) => {
      if (!userId || !cloneId) return null;
      // Chain onto the last feedback write so the backend reads fresh swipes.
      try {
        await lastWriteRef.current;
      } catch (_) {
        // a failed write is already surfaced by submitFeedback; still try.
      }
      setDistilling(true);
      try {
        const res = await cfg.distillFn({ cloneId, userId });
        return res?.data || null;
      } catch (err) {
        console.warn("[useCalibration] preference distillation failed:", err?.code, err?.message);
        return null;
      } finally {
        setDistilling(false);
      }
    },
    [userId, cfg],
  );

  // Pin / unpin a rejected sentence as a "worst miss". Writes ONLY the
  // calibration/hardRejects subpath so it never clobbers the per-category
  // swipe data a concurrent feedback flush is writing. We also patch the
  // shadow ref so a later swipe-flush (which carries hardRejects through
  // aggregateCalibrationEvents from the shadow base) keeps the pin instead
  // of overwriting it with a stale empty list.
  const toggleWorstReject = useCallback(
    async ({ cloneId, currentCalibration, text, categoryId }) => {
      if (!userId || !cloneId || !text) return null;
      const base =
        (lastWrittenRef.current.cloneId === cloneId &&
          lastWrittenRef.current.calibration) ||
        currentCalibration ||
        cfg.createEmpty();
      const next = cfg.toggleReject(base, { text, categoryId });
      try {
        await update(ref(database, `/World/${userId}/copyClones/${cloneId}`), {
          [`${cfg.rtdbKey}/hardRejects`]: next.hardRejects,
          updatedAt: Date.now(),
        });
        if (
          lastWrittenRef.current.cloneId === cloneId &&
          lastWrittenRef.current.calibration
        ) {
          lastWrittenRef.current = {
            cloneId,
            calibration: {
              ...lastWrittenRef.current.calibration,
              hardRejects: next.hardRejects,
            },
          };
        }
        return next.hardRejects;
      } catch (err) {
        console.warn("[useCalibration] worst-reject toggle failed:", err);
        setError(err.message || "Could not update worst misses");
        setErrorKind("write");
        return null;
      }
    },
    [userId, cfg],
  );

  // -------------------------------------------------------------------------
  // A/B DUEL REFINEMENT (body calibration only)
  // -------------------------------------------------------------------------

  // Generate challenger sentences for the clone's already-approved "champions".
  // Returns { duels: [{ id, category, champion:{text}, challenger:{text} }] }.
  const fetchDuels = useCallback(
    async ({ cloneId, language = "auto", maxDuels = 30 }) => {
      if (!userId || !cloneId) return null;
      setGenerating(true);
      setError(null);
      setErrorKind(null);
      try {
        const res = await duelsCallable({ cloneId, userId, language, maxDuels });
        const duels = res?.data?.duels;
        if (Array.isArray(duels) && duels.length) return duels;
        setError("No challengers could be generated. Try a normal round first.");
        setErrorKind("unknown");
        return null;
      } catch (err) {
        const cls = classifyError(err);
        console.warn("[useCalibration] duel generation failed:", err?.code, err?.message);
        setError(cls.friendly);
        setErrorKind(cls.kind);
        return null;
      } finally {
        setGenerating(false);
      }
    },
    [userId],
  );

  // Apply the user's A/B picks: winning challengers replace their champions,
  // losers are dropped. Writes the whole calibration block back in one patch
  // (same shadow-ref discipline as submitFeedback) and returns the new object.
  const submitDuelVerdicts = useCallback(
    async ({ cloneId, currentCalibration, verdicts }) => {
      if (!userId || !cloneId || !Array.isArray(verdicts) || verdicts.length === 0) {
        return null;
      }
      if (lastWrittenRef.current.cloneId !== cloneId) {
        lastWrittenRef.current = { cloneId, calibration: null };
      }
      const base = lastWrittenRef.current.calibration
        || currentCalibration
        || createEmptyCalibration();
      const next = applyCalibrationDuelVerdicts(base, verdicts);
      try {
        const writePromise = update(
          ref(database, `/World/${userId}/copyClones/${cloneId}`),
          { calibration: next, updatedAt: Date.now() },
        );
        lastWriteRef.current = writePromise;
        await writePromise;
        lastWrittenRef.current = { cloneId, calibration: next };
        return next;
      } catch (err) {
        console.warn("[useCalibration] duel verdict write failed:", err);
        setError(err.message || "Could not save your A/B picks");
        setErrorKind("write");
        return null;
      }
    },
    [userId],
  );

  // Clear the current error state without re-fetching — useful when the
  // user dismisses a non-blocking transport warning and wants the UI to
  // un-stick (e.g. close the error banner, keep the deck visible).
  const clearError = useCallback(() => {
    setError(null);
    setErrorKind(null);
  }, []);

  return {
    fetchSamples,
    fetchSamplesGrouped,
    fetchDuels,
    submitDuelVerdicts,
    submitFeedback,
    distillPreferences,
    toggleWorstReject,
    generating,
    genProgress,
    distilling,
    error,
    errorKind,
    clearError,
    lastBatch,
  };
};
